import { IModel } from '@common/models';
import { CacheUtils, ObjectMetadataManager, ObjectUtility } from '@common/utils';
import { isObservableObject, toJS } from 'mobx';
interface ICacheParams {
    key: string,
    value: IModel
}

const globalHeaders: { [key: string]: string } = {
}

const getFetchOptions = (): RequestInit => {
    return {
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            ...globalHeaders
        },
        redirect: 'follow', // manual, *follow, error
        referrerPolicy: 'origin',
    };
}

function getUrlWithQuery(url: string, query: any) {
    var queryString = Object.keys(query).map(key => key + '=' + query[key]).join('&');
    var ret = queryString ? url + '?' + queryString : url;
    return ret;
}

export function getRandomWaitInterval(minMilliseconds: number, maxMilliseconds: number) {
    // Generate a random number between 0 and 1
    var random = Math.random();

    // Calculate the random amount of milliseconds within the desired range
    var randomMilliseconds = Math.floor(random * (maxMilliseconds - minMilliseconds + 1)) + minMilliseconds;

    return randomMilliseconds;
}

export function getQueryParams(url: string | undefined = undefined): { [key: string]: string } {
    const search = url ? url : window.location.search.substring(1); // Get the query string without the "?" character
    const params = search.split('&'); // Split the query string into an array of key-value pairs
    const queryParams: { [key: string]: string } = {}; // Initialize an empty object to store the results
    for (let i = 0; i < params.length; i++) {
        const param = params[i].split('=');
        queryParams[param[0]] = decodeURIComponent(param[1]);
    }
    return queryParams;
}

export interface ISignalRConnectionHeaderValue {
    hubName: string,
    connectionId: string | null
}

export const SignalRRequestHeaderName = "SIGNALR_CONNECTIONS";
export interface IFetchParams {
    addQuery: (query: Object) => Omit<IFetchParams, "addQuery">;
    mergeOptions: (fetchOptions: RequestInit) => Omit<IFetchParams, "mergeOptions">;
    onError: (onError: (error: DOMException) => void) => Omit<IFetchParams, "onError">;
    get: <T>() => Promise<T>;
    getVersioned: <T extends IModel>(cacheKey: string) => Promise<T>;
    post: <T>(body: Object) => Promise<T>;
    put: <T>(body: Object) => Promise<T>;
    delete: () => Promise<boolean>;
    deleteWithResponse: <T>() => Promise<T>;
}
//I updated this to a class to get rid of the hacky casting so we'd actually get ts errors if we forget to implement something.
class FetchParams implements IFetchParams {
    constructor(url: string) {
        let method: "GET" | "PUT" | "POST" | "DELETE" = "GET";
        let queryObject: any = undefined;
        let bodyObject: Object | undefined = undefined;
        let options = getFetchOptions();
        let cacheParams: ICacheParams | undefined = undefined;
        let onErrorCallBack: ((error: DOMException) => void) | undefined = undefined;

        this.onError = (onError) => {
            if (onError)
                onErrorCallBack = onError;
            return this;
        }
        this.addQuery = (query: Object): Omit<IFetchParams, "addQuery"> => {
            if (!query)
                throw new DOMException('unable to addQuery, query is undefined or null', DOMException.NOT_SUPPORTED_ERR.toString());
            queryObject = query;

            return this;
        }
        this.mergeOptions = (fetchOptions: RequestInit): Omit<IFetchParams, "mergeOptions"> => {
            if (!fetchOptions)
                throw new DOMException('unable to mergeOptions, mergeOptions is undefined or null', DOMException.NOT_SUPPORTED_ERR.toString());
            options = { ...options, ...fetchOptions };
            return this;
        }
        this.get = function <T>(): Promise<T> {
            method = 'GET';
            const promise = run<T>();
            return promise;
        }
        this.getVersioned = function <T extends IModel>(cacheKey: string): Promise<T> {
            method = 'GET';
            // attempt to find a cached blob by the provided cache key
            return CacheUtils.get<T>(cacheKey)
                .then(value => {
                    // stash any found blob and its associated cache key
                    cacheParams = {
                        key: cacheKey,
                        value: value as T
                    }
                    // if there is a cached model and it has a rowversion, set the request if-none-match (etag) header.
                    if (cacheParams?.value?.rowVersion) {
                        options.headers = {
                            ...options.headers,
                            "if-none-match": cacheParams.value.rowVersion
                        }
                    }
                })
                // finally, run the fetch request.
                .then(() => run<T>())
        }
        this.put = function <T>(body: Object): Promise<T> {
            method = 'PUT';
            bodyObject = body;
            const promise = run<T>();
            return promise;
        }
        this.post = function <T>(body: Object): Promise<T> {
            method = 'POST';
            bodyObject = body;
            const promise = run<T>();
            return promise;
        }
        this.delete = function (): Promise<boolean> {
            method = 'DELETE';
            const promise = run<boolean>();
            return promise;
        }
        this.deleteWithResponse = function <T>(): Promise<T> {
            method = 'DELETE';
            const promise = run<T>();
            return promise;
        }

        let run = <T>(): Promise<T> => {
            if (queryObject) {
                queryObject = isObservableObject(queryObject) ? toJS(queryObject) : queryObject;
                url = getUrlWithQuery(url, queryObject)
            }
            if (bodyObject) {
                bodyObject = isObservableObject(bodyObject) ? toJS(bodyObject) : bodyObject;
                options = { ...options, body: JSON.stringify(bodyObject) }
            }
            options = { ...options, method: method };
            let promise = fetch(url, options);
            let retPromise = new Promise<T>((resolve, reject) => {
                promise.then((response) => {
                    if (response.status === 400) {
                        return response.json().then(exceptionStatus => {
                            if (Array.isArray(exceptionStatus)) {
                                const errorMessages = exceptionStatus.map((exceptionStatus) => {
                                    const message = ObjectUtility.getPropertyCaseInsensitive(exceptionStatus, "message")
                                    return message ? message : exceptionStatus;
                                }).join('\n ');
                                throw new DOMException(errorMessages);
                            }
                            else {
                                throw new DOMException(ObjectUtility.getPropertyCaseInsensitive(exceptionStatus, "message"));
                            }
                        });
                    }
                    else if (response.status === 404) {
                        throw new DOMException('Not Found')
                    }
                    else if (response.status === 409) {
                        throw new DOMException('Another user updated this record. Refresh your page to see the latest changes.')
                    }
                    // if the API returns a 304 Unmodified, return the cached blob
                    if (response.status === 304) {
                        return cacheParams?.value;
                    }
                    else if (response.status < 200 || response.status > 299) {
                        throw new DOMException('Request was not successful.');
                    }
                    const contentType = response.headers.get("content-type");
                    if (contentType && contentType.indexOf("application/json") > -1)
                        return response.json();
                    if (method === 'DELETE')
                        return true;
                    // response is a 200. Nothing was returned as part of the response. Just return it and let caller deal with it.
                    if (response.status === 200) {
                        return response;
                    }
                    throw new DOMException("Unknown http response, no handler for content type or method...", DOMException.NOT_SUPPORTED_ERR.toString());
                }).then((data) => {
                    ObjectMetadataManager.processApiResponseData<T>(data);
                    return data;
                }).then(data => {
                    // if we received a new cacheable blob, then cache it and resole the promise. 
                    if (cacheParams?.key) {
                        CacheUtils.set(cacheParams.key, data).then(resolve);
                        // otherwise just resolve the promise as normal.
                    } else {
                        resolve(data);
                    }
                }).catch((exception) => {
                    // pass exception to user-defined callback if provided,
                    // otherwise reject the promise
                    if (onErrorCallBack) {
                        onErrorCallBack(exception);
                    } else {
                        reject(exception);
                    }

                });
            });
            return retPromise;
        }
    }
    addQuery: (query: Object) => Omit<IFetchParams, 'addQuery'>;
    mergeOptions: (fetchOptions: RequestInit) => Omit<IFetchParams, 'mergeOptions'>;
    onError: (onError: (error: DOMException) => void) => Omit<IFetchParams, 'onError'>;
    get: <T>() => Promise<T>;
    getVersioned: <T extends IModel>(cacheKey: string) => Promise<T>;
    post: <T>(body: Object) => Promise<T>;
    put: <T>(body: Object) => Promise<T>;
    delete: () => Promise<boolean>;
    deleteWithResponse: <T>() => Promise<T>;


}
FetchParams.bind({});

function http(url: string): IFetchParams {
    let chainFluent = new FetchParams(url);
    return chainFluent;
}

//namespace merge some functions on the http function, so you can do 
//http.setGlobalHeader or http('url').put...
namespace http {
    export function setGlobalHeader(key: string, value: string) {
        globalHeaders[key] = value;
    }
    export function deleteGlobalHeader(key: string) {
        if (Object.prototype.hasOwnProperty.call(globalHeaders, key))
            delete globalHeaders[key];
    }
    /**
     * The server side services send signalr messages, we need them to know the connected client signalr connectionId (per request) so we can exclude connection ids from 
     * redundant group messages.  Otherwise the ajax call will trigger a refresh and then the signalr message will trigger another one.
     * This will set a header called "SIGNALR_CONNECTIONS" with a json value containing all the connected hubs and their connection ids.
     * @param hubName The name of the hub the connectionId is for
     * @param connectionId the connection id (or null) of the user.
     */
    export function updateSignalRConnection(hubName: string, connectionId: string | null) {
        if (!Object.prototype.hasOwnProperty.call(globalHeaders, SignalRRequestHeaderName))
            globalHeaders[SignalRRequestHeaderName] = JSON.stringify([]);
        var connections = JSON.parse(globalHeaders[SignalRRequestHeaderName]) as ISignalRConnectionHeaderValue[]

        let connection = _.first(connections.filter(a => _.isEqual(a.hubName, hubName)));

        if (!connectionId) {
            if (connection)
                _.remove(connections, connection);
        } else {
            if (!connection) {
                connection = { hubName, connectionId };
                connections.push(connection);
            }
            else
                connection.connectionId = connectionId;
        }

        globalHeaders[SignalRRequestHeaderName] = JSON.stringify(connections);
    }
}

export {
    http
}

export default http;
